import { FilterOperator, type FilterSet } from '../filters/types';
import { nameFilter } from '../filters/definitions/nameFilter';
import { nodeName, type AnyGraphNode } from '../types/graphNodes';
import { sortByRelevance, type SearchableNode } from '../utils/search';

/**
 * Operators whose string value expresses "find things like this". Derived from
 * the name filter's own operator list so a newly added affirmative operator is
 * relevance-ranked without touching this module; negative operators (and
 * structural filters — deity/ragam/…, bhajan id) carry no relevance signal, so
 * those result sets are shown alphabetically instead.
 */
const NEGATIVE_OPERATORS: ReadonlySet<FilterOperator> = new Set([
  FilterOperator.NotIs,
  FilterOperator.NotContains,
  FilterOperator.NotIn,
  FilterOperator.NotHas,
  FilterOperator.HasNone,
]);
const RELEVANCE_OPERATORS: ReadonlySet<FilterOperator> = new Set(
  nameFilter.supportedOperators.filter((op) => !NEGATIVE_OPERATORS.has(op))
);

/**
 * The search string of the first affirmative name filter, if any. Only such a
 * query justifies relevance ordering of browse results.
 */
export function getRelevanceQuery(filters: FilterSet): string | undefined {
  for (const f of filters) {
    if (f.filterId !== 'name' || !RELEVANCE_OPERATORS.has(f.operator)) continue;
    if (typeof f.value === 'string' && f.value.trim()) return f.value;
  }
  return undefined;
}

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

/**
 * Catalog convention: a value named "." or "missing" is a placeholder for
 * absent data (see jnana-native's onFetchRagams, which filters the same
 * markers). Anything with no letters or digits at all gets the same
 * treatment — browse lists show these last, never first.
 */
export function isMissingName(name: string | undefined): boolean {
  const n = (name ?? '').trim().toLowerCase();
  return !n || n === 'missing' || !/[\p{L}\p{N}]/u.test(n);
}

/**
 * The one definition of "alphabetical" for browse surfaces: case-,
 * diacritic- and number-aware ("part 2" < "part 10") name comparison,
 * with missing-marker names sunk to the very bottom.
 */
export function compareDisplayNames(a: string, b: string): number {
  const aMissing = isMissingName(a);
  const bMissing = isMissingName(b);
  if (aMissing !== bMissing) return aMissing ? 1 : -1;
  return collator.compare(a, b);
}

/** The minimal node shape orderResults needs; every catalog node satisfies it. */
interface OrderableNode {
  nodeId: number;
  data?: unknown;
}

/**
 * Order browse results for display: alphabetical by the title the card shows,
 * with relevance ranking layered on top only while an affirmative search query
 * is active. Relevance input is pre-alphabetized so score ties and unranked
 * leftovers both come out alphabetical rather than in catalog order.
 */
export function orderResults<T extends OrderableNode>(nodes: T[], filters: FilterSet): T[] {
  // nodeName/sortByRelevance only read nodeId + data name fields, which every
  // node shape here carries — the loose bound keeps call sites cast-free.
  const decorated = nodes.map((node) => ({
    node,
    key: nodeName(node as unknown as AnyGraphNode)?.trim() ?? '',
  }));
  // compareDisplayNames already sinks nameless and missing-marker nodes.
  decorated.sort(
    (a, b) => compareDisplayNames(a.key, b.key) || a.node.nodeId - b.node.nodeId
  );
  const alphabetical = decorated.map((d) => d.node);

  const query = getRelevanceQuery(filters);
  if (!query) return alphabetical;
  return sortByRelevance(alphabetical as unknown as SearchableNode[], query) as unknown as T[];
}
